import { useCallback, useMemo } from 'react';

import { NodeSelection } from 'prosemirror-state';

import type { EditorActions } from '@atlaskit/editor-core';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { useExtensionsActions } from '@atlassian/company-hub-common';

import { ALLOWED_EXTENSIONS } from './constants';

const UFOPRESS_EVENT = 'editor-config-panel-edit';

type useHubEditorExtensionConfigPanelResult = {
	closeConfigPanel: (localId?: string) => void;
	openConfigPanel: (localId: string) => void;
};

export const useHubEditorExtensionConfigPanel = (
	editorActions: EditorActions,
): useHubEditorExtensionConfigPanelResult => {
	const { setSelectedExtensionLocalId } = useExtensionsActions();

	const openConfigPanel = useCallback(
		(localId: string) => {
			if (!localId) {
				return;
			}

			const { attrs, type } = editorActions?.getNodeByLocalId(localId)?.toJSON() || {};
			const { extensionType, extensionKey } = attrs || {};

			const editorView = editorActions._privateGetEditorView();
			const selection = editorView?.state?.selection;

			if (selection instanceof NodeSelection) {
				if (ALLOWED_EXTENSIONS.includes(extensionType)) {
					traceUFOPress(UFOPRESS_EVENT);
					setSelectedExtensionLocalId(localId);
				}
			} else {
				if (type !== 'multiBodiedExtension' || !editorView?.state) {
					return;
				}

				// Handle MultiBodiedExtension config panel
				let multiBodiedExtensionPosition = editorView.state.selection.from;
				editorView.state.tr.doc.descendants((node: Node, pos: number) => {
					if (node.attrs?.localId === localId) {
						multiBodiedExtensionPosition = pos;
						return;
					}
				});

				editorView.dispatch(
					editorView.state.tr?.setSelection?.(
						NodeSelection.create(editorView.state.tr.doc, multiBodiedExtensionPosition),
					),
				);

				if (ALLOWED_EXTENSIONS.includes(extensionType)) {
					traceUFOPress(UFOPRESS_EVENT);
					setSelectedExtensionLocalId(localId);
				}
			}

			if (!!extensionKey && !!extensionType) {
				// Todo: Fire analytics event callback
			}
		},
		[setSelectedExtensionLocalId, editorActions],
	);

	const closeConfigPanel = useCallback(() => {
		setSelectedExtensionLocalId(null);
	}, [setSelectedExtensionLocalId]);

	return useMemo(
		() => ({
			openConfigPanel,
			closeConfigPanel,
		}),
		[openConfigPanel, closeConfigPanel],
	);
};
